import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Input, Select, Button, message, Row, Col, Upload, Card, Divider, Space } from 'antd';
import { UploadOutlined, SaveOutlined, CloseOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { addDepartment, updateDepartment, fetchSpecialities, fetchDepartmentDoctors } from '../../actions/DepartmentManagementAction';
import RichTextEditor from './RichTextEditor';

const { TextArea } = Input;
const { Option } = Select;

const AddEditDepartmentModal = ({ visible, onCancel, onSuccess, department, mode }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [specialitiesLoading, setSpecialitiesLoading] = useState(false);
  const [consultationTypes, setConsultationTypes] = useState([]);
  const [editingConsultationType, setEditingConsultationType] = useState(null);
  const [editingDataMap, setEditingDataMap] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  
  const { specialities } = useSelector(state => state.departmentManagement);

  // Predefined consultation type options
  const consultationTypeOptions = [
    { value: 'First time consultation', label: 'First time consultation' },
    { value: 'Follow Up', label: 'Follow Up' },
    { value: 'Second Opinion', label: 'Second Opinion' },
    { value: 'Emergency consultation', label: 'Emergency consultation' },
    { value: 'Video consultation', label: 'Video consultation' },
    { value: 'Telemedicine consultation', label: 'Telemedicine consultation' },
    { value: 'Specialist consultation', label: 'Specialist consultation' },
    { value: 'General consultation', label: 'General consultation' }
  ];

  // Fetch specialities when modal opens
  useEffect(() => {
    if (visible && specialities.length === 0) {
      setSpecialitiesLoading(true);
      dispatch(fetchSpecialities())
        .then(() => {
          setSpecialitiesLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching specialities:", error);
          setSpecialitiesLoading(false);
        });
    }
  }, [visible, dispatch, specialities.length]);

   console.error("specialities", specialities);
  // Set default speciality when specialities are loaded
  useEffect(() => {
    if (visible && mode === 'add' && specialities.length > 0 && !form.getFieldValue('ssm_primary_speciality_id')) {
      form.setFieldsValue({
        ssm_primary_speciality_id: specialities[0].specialityId
      });
    }
  }, [visible, mode, specialities, form]);

  useEffect(() => {
    if (visible && department && mode === 'edit') {
      form.setFieldsValue({
        ssm_secondary_speciality_name: department.ssm_secondary_speciality_name,
        ssm_primary_speciality_id: department.ssm_primary_speciality_id,
        ssm_details: department.ssm_details,
        ssm_icon: department.ssm_icon,
        ssm_status: department.ssm_status || 'Active'
      });
      // Load consultation types if they exist
      if (department.consultationTypes) {
        // Ensure each consultation type has a unique ID
        const consultationTypesWithIds = department.consultationTypes.map((type, index) => ({
          ...type,
          id: type.id || `existing_consultation_${Date.now()}_${index}`
        }));
        console.log('Loaded consultation types with IDs:', consultationTypesWithIds);
        setConsultationTypes(consultationTypesWithIds);
      }
      // Note: Selected doctors are now handled in fetchDoctors function
      // to ensure we get the existing doctors from the department.doctorslist
    } else if (visible && mode === 'add') {
      form.resetFields();
      form.setFieldsValue({
        ssm_status: 'Active'
      });
      setConsultationTypes([]);
      setSelectedDoctors([]);
    }
  }, [visible, department, mode, form]);

  // Fetch doctors when modal opens and department data is available
  useEffect(() => {
    if (visible) {
      // Small delay to ensure department data is loaded first
      setTimeout(() => {
        fetchDoctors();
      }, 100);
    }
  }, [visible, department]);

  const fetchDoctors = async () => {
    try {
      setDoctorsLoading(true);
      // Fetch all available doctors
      const doctorsList = await dispatch(fetchDepartmentDoctors());
      console.log('Fetched doctors list:', doctorsList);
      setDoctors(doctorsList);
      
      // If editing and department has existing doctors, set them as selected
      if (mode === 'edit' && department && department.doctorslist) {
        console.log('Department existing doctors:', department.doctorslist);
        const existingDoctorIds = department.doctorslist.map(doctor => 
          doctor.doctorId || doctor.id || doctor.doctor_id
        ).filter(id => id); // Remove any undefined values
        console.log('Existing doctor IDs:', existingDoctorIds);
        setSelectedDoctors(existingDoctorIds);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
      message.error("Failed to fetch doctors list");
    } finally {
      setDoctorsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Validate that primary speciality is selected
      if (!values.ssm_primary_speciality_id) {
        message.error('Please select a primary speciality');
        setLoading(false);
        return;
      }

      // Ensure primary speciality ID is set from dropdown selection
      const departmentData = {
        ...values,
        ssm_primary_speciality_id: values.ssm_primary_speciality_id,
        consultationTypes: consultationTypes.filter(type => type.consultationType && type.consultationType.trim()),
        selectedDoctors: selectedDoctors
      };

      console.log('Submitting department data:', departmentData);

      if (mode === 'edit' && department) {
        departmentData.ssm_id = department.ssm_id;
        await dispatch(updateDepartment(departmentData));
      } else {
        await dispatch(addDepartment(departmentData));
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error submitting department:', error);
      message.error('Failed to save department');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setConsultationTypes([]);
    setEditingConsultationType(null);
    setEditingDataMap({});
    onCancel();
  };

  // Consultation Type Management Functions
  const addConsultationType = () => {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9);
    const newConsultationType = {
      id: `consultation_${timestamp}_${randomId}`,
      consultationType: undefined, // Will be selected from dropdown
      domesticCost: '',
      internationalCost: '',
      duration: '',
      description: '',
      sharePercentage: '0',
      gstPercentage: '0'
    };
    console.log('Adding new consultation type with ID:', newConsultationType.id);
    setConsultationTypes(prev => [...prev, newConsultationType]);
    setEditingConsultationType(newConsultationType.id);
    setEditingDataMap(prev => ({
      ...prev,
      [newConsultationType.id]: { ...newConsultationType }
    }));
  };

  const editConsultationType = (id) => {
    console.log('Editing consultation type:', id);
    const consultationType = consultationTypes.find(type => type.id === id);
    console.log('Found consultation type:', consultationType);
    setEditingConsultationType(id);
    setEditingDataMap(prev => {
      const newMap = {
        ...prev,
        [id]: { ...consultationType }
      };
      console.log('New editing data map:', newMap);
      return newMap;
    });
  };

  const deleteConsultationType = (id) => {
    setConsultationTypes(consultationTypes.filter(type => type.id !== id));
    if (editingConsultationType === id) {
      setEditingConsultationType(null);
    }
    setEditingDataMap(prev => {
      const newMap = { ...prev };
      delete newMap[id];
      return newMap;
    });
  };

  const updateConsultationType = (id, field, value) => {
    console.log('Updating consultation type:', id, field, value);
    console.log('Current editingDataMap:', editingDataMap);
    
    setEditingDataMap(prev => {
      const newMap = {
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value
        }
      };
      console.log('New editingDataMap:', newMap);
      return newMap;
    });
  };

  const saveConsultationType = (id) => {
    const editingData = editingDataMap[id];
    if (editingData && editingData.consultationType && editingData.consultationType.trim()) {
      setConsultationTypes(consultationTypes.map(type => 
        type.id === id ? { ...editingData } : type
      ));
      setEditingConsultationType(null);
      setEditingDataMap(prev => {
        const newMap = { ...prev };
        delete newMap[id];
        return newMap;
      });
    } else {
      message.error('Please select a consultation type');
    }
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };
  console.log("specialities", specialities);

  return (
    <Modal
      title={`${mode === 'add' ? 'Add New' : 'Edit'} Department`}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1000}
      destroyOnClose
      className="department-modal"
      style={{ top: 20 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          ssm_status: 'Active'
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="ssm_secondary_speciality_name"
              label="Department Name"
              rules={[
                { required: true, message: 'Please enter department name' },
                { max: 255, message: 'Department name cannot exceed 255 characters' }
              ]}
            >
              <Input placeholder="Enter department name" />
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={12}>
            <Form.Item
              name="ssm_primary_speciality_id"
              label="Primary Speciality"
              rules={[
                { required: true, message: 'Please select speciality' },
              ]}
            >
              <Select
                placeholder="Select speciality"
                loading={specialitiesLoading}
                showSearch
                allowClear={false}
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(val) => console.log("Selected value:", val)}
              >
                {specialities.map((speciality) => (
                  <Option key={speciality.specialityId} value={speciality.specialityId}>
                    {speciality.specialityName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="ssm_status"
              label="Status"
              rules={[
                { required: true, message: 'Please select status' }
              ]}
            >
              <Select placeholder="Select status">
                <Option value="">Select Status</Option>
                <Option value="Active">Active</Option>
                <Option value="Inactive">Inactive</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Assign Doctors"
          help="Select doctors to assign to this department"
        >
          <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666',display:"none" }}>
            Available doctors: {doctors.length} | Selected: {selectedDoctors.length}
            {selectedDoctors.length > 0 && (
              <div style={{ marginTop: '4px' }}>
                Selected: {selectedDoctors.map(id => {
                  const doctor = doctors.find(d => (d.doctorId || d.id) === id);
                  return doctor ? (doctor.doctorName || doctor.name) : id;
                }).join(', ')}
              </div>
            )}
          </div>
          <Select
            mode="multiple"
            placeholder="Select doctors"
             className="select-doctor"
            value={selectedDoctors}
            onChange={(values) => {
              console.log('Selected doctors changed:', values);
              setSelectedDoctors(values);
            }}
            loading={doctorsLoading}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
            optionLabelProp="label"
            maxTagCount="responsive"
            maxTagTextLength={80}
            tagRender={(props) => {
              const { label, closable, onClose } = props;
              const doctor = doctors.find(d => (d.doctorId || d.id) === props.value);
              const displayName = doctor ? (doctor.doctorName || doctor.name) : label;
              return (
                <span
                  style={{
                    background: '#406076',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    margin: '2px 4px 2px 0',
                    fontSize: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    maxWidth: '250px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span style={{ flex: '1', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {displayName}
                  </span>
                  {closable && (
                    <span
                      onClick={onClose}
                      style={{
                        marginLeft: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: '1'
                      }}
                    >
                      ×
                    </span>
                  )}
                </span>
              );
            }}
          >
            {doctors.map((doctor) => {
              const doctorId = doctor.doctorId || doctor.id;
              const doctorName = doctor.doctorName || doctor.name || 'Unknown Doctor';
              const specialization = doctor.specialization || doctor.speciality || 'General';
              const fullDisplayName = `${doctorName} - ${specialization}`;
              const isSelected = selectedDoctors.includes(doctorId);
              console.log(`Doctor ${doctorName} (${doctorId}) selected: ${isSelected}`);
              return (
                <Option 
                  key={doctorId} 
                  value={doctorId}
                  label={fullDisplayName}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    width: '100%',
                    minWidth: '300px'
                  }}>
                    <span style={{ 
                      fontWeight: '500', 
                      color: '#262626',
                      flex: '1',
                      minWidth: '0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {doctorName}
                    </span>
                    <span style={{ 
                      color: '#8c8c8c', 
                      fontSize: '12px',
                      marginLeft: '8px',
                      flexShrink: '0'
                    }}>
                      {specialization}
                    </span>
                  </div>
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        <Form.Item
          name="ssm_details"
          label="Department Details"
          rules={[
            { max: 10000, message: 'Details cannot exceed 10000 characters' }
          ]}
        >
          <RichTextEditor 
            placeholder="Enter department details and description"
            rows={6}
          />
        </Form.Item>

        <Form.Item
          name="ssm_icon"
          label="Icon URL"
          rules={[
            { max: 255, message: 'Icon URL cannot exceed 255 characters' }
          ]}
        >
          <Input placeholder="Enter icon URL or file path" />
        </Form.Item>

        <Form.Item
          name="icon_upload"
          label="Upload Icon"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          style={{display: 'none'}}
        >
          <Upload
            name="icon"
            listType="picture"
            maxCount={1}
            beforeUpload={() => false}
          >
            <Button icon={<UploadOutlined />}>Upload Icon</Button>
          </Upload>
        </Form.Item>

        <Divider>Consultation Types</Divider>

       

        <Card 
          title="Manage Consultation Types"
          extra={
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={addConsultationType}
              size="small"
            >
              Add Consultation Type
            </Button>
          }
          className="consultation-types-card"
        >
          {consultationTypes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#8c8c8c' }}>
              No consultation types added yet. Click "Add Consultation Type" to get started.
            </div>
          ) : (
            <div className="consultation-types-list">
              {consultationTypes.map((consultationType, index) => {
                // Ensure consultation type has an ID
                const consultationTypeWithId = {
                  ...consultationType,
                  id: consultationType.id || `fallback_consultation_${Date.now()}_${index}`
                };
                
                console.log('Rendering consultation type:', consultationTypeWithId.id, 'at index:', index);
                console.log('Is editing this one?', editingConsultationType === consultationTypeWithId.id);
                console.log('Editing data for this one:', editingDataMap[consultationTypeWithId.id]);
                
                return (
                  <Card 
                    key={`${consultationTypeWithId.id}-${index}`} 
                    size="small" 
                    className="consultation-type-item"
                    style={{ marginBottom: '16px' }}
                  >
                  <div className="consultation-type-header">
                    <div className="consultation-type-title">
                      {editingConsultationType === consultationTypeWithId.id ? (
                        <Select
                          key={`consultation-type-${consultationTypeWithId.id}`}
                          value={editingDataMap[consultationTypeWithId.id]?.consultationType || undefined}
                          onChange={(value) => {
                            console.log('Select change for consultation type:', consultationTypeWithId.id, value);
                            updateConsultationType(consultationTypeWithId.id, 'consultationType', value);
                          }}
                          placeholder="Select consultation type"
                          style={{ width: '300px' }}
                          showSearch
                          allowClear
                          filterOption={(input, option) =>
                            (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {consultationTypeOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      ) : (
                        <strong>{consultationTypeWithId.consultationType || 'Unnamed Consultation Type'}</strong>
                      )}
                    </div>
                    <Space>
                      {editingConsultationType === consultationTypeWithId.id ? (
                        <Button 
                          type="primary" 
                          size="small" 
                          onClick={() => saveConsultationType(consultationTypeWithId.id)}
                        >
                          Save
                        </Button>
                      ) : (
                        <Button 
                          icon={<EditOutlined />} 
                          size="small" 
                          onClick={() => editConsultationType(consultationTypeWithId.id)}
                        >
                          Edit
                        </Button>
                      )}
                      <Button 
                        danger 
                        icon={<DeleteOutlined />} 
                        size="small" 
                        onClick={() => deleteConsultationType(consultationTypeWithId.id)}
                      >
                        Delete
                      </Button>
                    </Space>
                  </div>

                  {editingConsultationType === consultationTypeWithId.id && (
                    <div className="consultation-type-fields" style={{ marginTop: '16px' }}>
                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                              Domestic Cost (₹)
                            </label>
                            <Input
                              key={`domestic-cost-${consultationTypeWithId.id}`}
                              value={editingDataMap[consultationTypeWithId.id]?.domesticCost || ''}
                              onChange={(e) => updateConsultationType(consultationTypeWithId.id, 'domesticCost', e.target.value)}
                              placeholder="Enter domestic cost"
                              type="number"
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                              International Cost (₹)
                            </label>
                            <Input
                              key={`international-cost-${consultationTypeWithId.id}`}
                              value={editingDataMap[consultationTypeWithId.id]?.internationalCost || ''}
                              onChange={(e) => updateConsultationType(consultationTypeWithId.id, 'internationalCost', e.target.value)}
                              placeholder="Enter international cost"
                              type="number"
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                              Duration (minutes)
                            </label>
                            <Input
                              key={`duration-${consultationTypeWithId.id}`}
                              value={editingDataMap[consultationTypeWithId.id]?.duration || ''}
                              onChange={(e) => updateConsultationType(consultationTypeWithId.id, 'duration', e.target.value)}
                              placeholder="Enter duration in minutes"
                              type="number"
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                              Description
                            </label>
                            <Input
                              key={`description-${consultationTypeWithId.id}`}
                              value={editingDataMap[consultationTypeWithId.id]?.description || ''}
                              onChange={(e) => updateConsultationType(consultationTypeWithId.id, 'description', e.target.value)}
                              placeholder="Enter description"
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                              Share Percentage (%)
                            </label>
                            <Input
                              key={`sharePercentage-${consultationTypeWithId.id}`}
                              value={editingDataMap[consultationTypeWithId.id]?.sharePercentage || '0'}
                              onChange={(e) => updateConsultationType(consultationTypeWithId.id, 'sharePercentage', e.target.value)}
                              placeholder="Enter share percentage"
                              type="number"
                              min="0"
                              max="100"
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                              GST Percentage (%)
                            </label>
                            <Input
                              key={`gstPercentage-${consultationTypeWithId.id}`}
                              value={editingDataMap[consultationTypeWithId.id]?.gstPercentage || '0'}
                              onChange={(e) => updateConsultationType(consultationTypeWithId.id, 'gstPercentage', e.target.value)}
                              placeholder="Enter GST percentage"
                              type="number"
                              min="0"
                              max="100"
                            />
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {editingConsultationType !== consultationTypeWithId.id && consultationTypeWithId.consultationType && (
                    <div className="consultation-type-summary" style={{ marginTop: '12px' }}>
                      <Row gutter={16}>
                        {consultationTypeWithId.domesticCost && (
                          <Col xs={12} sm={6}>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                              Domestic: ₹{consultationTypeWithId.domesticCost}
                            </div>
                          </Col>
                        )}
                        {consultationTypeWithId.internationalCost && (
                          <Col xs={12} sm={6}>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                              International: ₹{consultationTypeWithId.internationalCost}
                            </div>
                          </Col>
                        )}
                        {consultationTypeWithId.duration && (
                          <Col xs={12} sm={6}>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                              Duration: {consultationTypeWithId.duration} mins
                            </div>
                          </Col>
                        )}
                        {consultationTypeWithId.description && (
                          <Col xs={12} sm={6}>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                              {consultationTypeWithId.description}
                            </div>
                          </Col>
                        )}
                        {consultationTypeWithId.sharePercentage && (
                          <Col xs={12} sm={6}>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                              Share: {consultationTypeWithId.sharePercentage}%
                            </div>
                          </Col>
                        )}
                        {consultationTypeWithId.gstPercentage && (
                          <Col xs={12} sm={6}>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                              GST: {consultationTypeWithId.gstPercentage}%
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  )}
                </Card>
              );
              })}
            </div>
          )}
        </Card>

        <div className="modal-footer">
          <Button 
            onClick={handleCancel} 
            icon={<CloseOutlined />}
            size="large"
          >
            Cancel
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            icon={<SaveOutlined />}
            size="large"
          >
            {mode === 'add' ? 'Add Department' : 'Update Department'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddEditDepartmentModal;
